import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Bot, Headset, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SID_KEY = "michamou_support_sid";
function getSid() {
  let s = localStorage.getItem(SID_KEY);
  if (!s) {
    s = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(SID_KEY, s);
  }
  return s;
}

const FAQ_CHIPS = [
  "Como crio um evento?",
  "Quais são os planos?",
  "Como formatar meu repertório?",
  "Como funcionam os pagamentos via Pix?",
];

export default function SupportChat() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const hideOnRoute = pathname.startsWith("/mensagens") || pathname.startsWith("/completar-perfil");
  const firstName = (user && (user.stage_name || user.full_name) || "").trim().split(" ")[0];
  const hello = {
    role: "assistant",
    content: firstName
      ? `Olá, ${firstName}! Sou o assistente do Mí Chamou 🎵 Como posso te ajudar? (eventos, planos, repertório, pagamentos...)`
      : "Olá! Sou o assistente do Mí Chamou 🎵 Como posso te ajudar? (eventos, planos, repertório, pagamentos...)",
  };

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(() => { try { const p = JSON.parse(localStorage.getItem("michamou_support_pos")); return p && typeof p.x === "number" ? p : null; } catch { return null; } });
  const dragRef = useRef({ dragging: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const [msgs, setMsgs] = useState([hello]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketEmail, setTicketEmail] = useState("");
  const [ticketName, setTicketName] = useState("");
  const [ticketBusy, setTicketBusy] = useState(false);
  const [ticketDone, setTicketDone] = useState(false);
  const bodyRef = useRef(null);
  const sid = useRef(getSid());

  const scrollDown = () => setTimeout(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, 50);

  useEffect(() => {
    // Keep greeting in sync with auth once user resolves (only if still default single message)
    setMsgs((m) => (m.length === 1 && m[0].role === "assistant" ? [hello] : m));
    if (user) {
      setTicketEmail((v) => v || user.email || "");
      setTicketName((v) => v || user.stage_name || user.full_name || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (open && !loaded) {
      setLoaded(true);
      api.get(`/support/history?session_id=${sid.current}`).then((r) => {
        const h = r.data?.messages || [];
        if (h.length) setMsgs(h);
      }).catch(() => {}).finally(scrollDown);
    }
    if (open) scrollDown();
  }, [open, loaded]);

  const sendText = async (text) => {
    const t = (text || "").trim();
    if (!t || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: t }]);
    setBusy(true);
    scrollDown();
    try {
      const { data } = await api.post("/support/chat", { session_id: sid.current, message: t });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Ops, não consegui responder agora. Tente novamente em instantes." }]);
    } finally {
      setBusy(false);
      scrollDown();
    }
  };

  const submitTicket = async () => {
    if (ticketBusy) return;
    const email = ticketEmail.trim();
    if (!email || !/.+@.+\..+/.test(email)) return;
    setTicketBusy(true);
    try {
      await api.post("/support/ticket", {
        session_id: sid.current,
        email,
        name: ticketName.trim(),
        subject: "Solicitação de atendimento humano",
      });
      setTicketDone(true);
    } catch (e) {
      setTicketDone(false);
      alert("Não foi possível abrir o atendimento agora. Tente novamente em instantes.");
    } finally {
      setTicketBusy(false);
    }
  };

  const showChips = msgs.length <= 1 && !busy;

  const clampPos = (x, y) => {
    const s = 56, m = 8;
    const maxX = window.innerWidth - s - m, maxY = window.innerHeight - s - m;
    return { x: Math.max(m, Math.min(x, maxX)), y: Math.max(m, Math.min(y, maxY)) };
  };
  useEffect(() => {
    const move = (e) => { const d = dragRef.current; if (!d.dragging) return; const t = e.touches ? e.touches[0] : e; const dx = t.clientX - d.sx, dy = t.clientY - d.sy; if (Math.abs(dx) > 5 || Math.abs(dy) > 5) d.moved = true; if (e.cancelable) e.preventDefault(); setPos(clampPos(d.ox + dx, d.oy + dy)); };
    const up = () => { const d = dragRef.current; if (!d.dragging) return; d.dragging = false; if (d.moved) setPos((p) => { if (p) localStorage.setItem("michamou_support_pos", JSON.stringify(p)); return p; }); else setOpen(true); };
    const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up); window.removeEventListener("resize", onResize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hideOnRoute) return null;

  return (
    <>
      {!open && (
        <button data-testid="support-launcher" aria-label="Abrir chat de ajuda"
          onMouseDown={(e) => { const r = e.currentTarget.getBoundingClientRect(); dragRef.current = { dragging: true, moved: false, sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top }; }}
          onTouchStart={(e) => { const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect(); dragRef.current = { dragging: true, moved: false, sx: t.clientX, sy: t.clientY, ox: r.left, oy: r.top }; }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
          style={pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : undefined}
          className={`fixed z-[60] ${pos ? "" : "bottom-24 right-4 sm:bottom-6 sm:right-6"} h-14 w-14 rounded-full bg-[#FF4D00] text-white shadow-lg shadow-black/40 flex items-center justify-center hover:scale-105 transition-transform cursor-grab active:cursor-grabbing touch-none select-none`}>
          <MessageCircle className="h-6 w-6 pointer-events-none" />
        </button>
      )}

      {open && (
        <div data-testid="support-panel"
          className="fixed z-[60] bottom-24 right-4 sm:bottom-6 sm:right-6 w-[min(92vw,380px)] h-[min(70vh,560px)] rounded-2xl overflow-hidden flex flex-col border border-white/10 bg-[#121217] text-zinc-100 shadow-2xl shadow-black/60">
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0d0d0f] border-b border-white/10">
            <span className="h-8 w-8 rounded-full bg-[#FF4D00]/20 text-[#FF4D00] flex items-center justify-center"><Bot className="h-5 w-5" /></span>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight">Ajuda Mí Chamou</div>
              <div className="text-[11px] text-emerald-400 leading-tight">Assistente online</div>
            </div>
            <button data-testid="support-attendant-top" onClick={() => { setTicketOpen(true); setTicketDone(false); }} aria-label="Falar com atendente"
              className="ml-auto flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] font-medium text-zinc-200 hover:bg-white/10 transition-colors">
              <Headset className="h-4 w-4" /> Atendente
            </button>
            <button data-testid="support-close" onClick={() => setOpen(false)} aria-label="Fechar chat" className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center"><X className="h-5 w-5" /></button>
          </div>

          <div ref={bodyRef} data-testid="support-messages" className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-[#FF4D00] text-white rounded-br-sm" : "bg-white/5 text-zinc-100 rounded-bl-sm"}`}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white/5 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Digitando…</div></div>}

            {showChips && (
              <div data-testid="support-faq-chips" className="pt-1 flex flex-wrap gap-2">
                {FAQ_CHIPS.map((q) => (
                  <button key={q} data-testid={`support-chip-${q.slice(0, 12)}`} onClick={() => sendText(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#FF4D00]/40 text-[#FF7A3D] hover:bg-[#FF4D00]/10 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {ticketOpen && (
            <div data-testid="support-ticket-form" className="border-t border-white/10 bg-[#0d0d0f] px-4 py-3 space-y-2">
              {ticketDone ? (
                <div data-testid="support-ticket-done" className="flex items-start gap-2 text-sm text-emerald-400">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <span>Recebemos sua solicitação! Nossa equipe vai responder pelo e-mail informado em breve.</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold flex items-center gap-1.5"><Headset className="h-4 w-4 text-[#FF4D00]" /> Falar com atendente</div>
                    <button data-testid="support-ticket-cancel" onClick={() => setTicketOpen(false)} className="text-xs text-zinc-400 hover:text-zinc-200">Cancelar</button>
                  </div>
                  <p className="text-[11px] text-zinc-400">A conversa atual será enviada à nossa equipe. Deixe seu contato:</p>
                  <input data-testid="support-ticket-name" value={ticketName} onChange={(e) => setTicketName(e.target.value)} placeholder="Seu nome"
                    className="w-full bg-[#09090B] border border-white/10 rounded-md px-3 h-9 text-sm outline-none focus:border-[#FF4D00]/50" />
                  <input data-testid="support-ticket-email" type="email" value={ticketEmail} onChange={(e) => setTicketEmail(e.target.value)} placeholder="seu@email.com"
                    className="w-full bg-[#09090B] border border-white/10 rounded-md px-3 h-9 text-sm outline-none focus:border-[#FF4D00]/50" />
                  <button data-testid="support-ticket-submit" onClick={submitTicket} disabled={ticketBusy || !/.+@.+\..+/.test(ticketEmail.trim())}
                    className="w-full h-9 rounded-md bg-[#FF4D00] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40">
                    {ticketBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar para o atendimento
                  </button>
                </>
              )}
            </div>
          )}

          <div className="p-2.5 border-t border-white/10 flex items-center gap-2">
            <input data-testid="support-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(input); } }}
              placeholder="Escreva sua dúvida..." className="flex-1 bg-[#09090B] border border-white/10 rounded-full px-4 h-10 text-sm outline-none focus:border-[#FF4D00]/50" />
            <button data-testid="support-send" onClick={() => sendText(input)} disabled={busy || !input.trim()} aria-label="Enviar"
              className="h-10 w-10 rounded-full bg-[#FF4D00] text-white flex items-center justify-center disabled:opacity-40 shrink-0"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
